import {
  Component, OnInit, ChangeDetectorRef, ViewChild, ChangeDetectionStrategy,
  ViewChildren, QueryList
} from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, FormArray, Validators, AbstractControl } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import * as _ from 'lodash';

import { BrandService } from 'shared/brand/brand.service';
import { ScorecardConnectorService } from 'shared/scorecard/scorecard-connector.service';
import { CollapseGroupComponent } from 'shared/collapse/collapse-group/collapse-group.component';
import {
  convertPackagesDataToArray,
  convertPackagesScoreDataToArray,
  getPackagesSegmentDataForType,
  getPackageScoreItemPath,
  getAvailableSegmentScoreTypes,
} from 'shared/product/product-helpers';
import { packageTypeLookup } from 'shared/product/product-lookups';

import { SegmentFieldItem } from 'utils/segment-field-item.interface';
import { getFieldRequiredValue } from 'utils/scorecard-conditionals';
import { packagesSegmentData } from '../../segment-data/packages-segment-data';
import { PackageModel } from '../../models/package.model';
import { BaseSegmentListComponent } from '../base/_base-segment-list.component';
import { segmentAnimation } from '../base/animations';
import {
  rowData, detailsData, detailsShippingData, hazardousData, packingData,
  collapseWidthPx,
} from './table-data';
import { ExtendedInformationComponent } from './extended-information/extended-information.component';
import { takeWhile } from 'rxjs/operators';


const metrics = {
  STANDART: 'standart',
  METRIC: 'metric',
};

@Component({
  moduleId: module.id,
  selector: 'packages-segment',
  templateUrl: 'packages.component.html',
  styleUrls: ['../base/_base-segment.component.scss', './packages.component.scss'],
  animations: [segmentAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PackagesComponent extends BaseSegmentListComponent implements OnInit {
  rowData: any[] = rowData;
  detailsData: any[] = detailsData;
  detailsShippingData: any[] = detailsShippingData;
  hazardousData: any[] = hazardousData;
  packingData: any[] = packingData;
  collapseWidth = collapseWidthPx;
  metricSystem: string;

  // define in the Child
  declare segmentForm: UntypedFormGroup;
  segmentName: string = 'packages'; // used in save request
  pathToScores: string = 'segments.packages.tallies';
  controlClassPrefix: string = 'packages-control-row-';

  segmentData: any = packagesSegmentData;
  segmentDataForTypes: any = {};
  fieldNamesWithoutPrefix: boolean = true;

  units: Array<any> = [{
    option: metrics.METRIC,
    title: 'Metric',
  },
  {
    option:  metrics.STANDART,
    title: 'Standard',
  }];

  dimensionsFieldMaps = ['dimensionsHeight', 'dimensionsLength', 'dimensionsWidth',
    'shippingLength', 'shippingWidth', 'shippingHeight'];
  weightsFieldMaps = ['dimensionalWeight', 'weight'];

  @ViewChild(ExtendedInformationComponent, {static: false}) extInfo: ExtendedInformationComponent | void;

  // view more collapse groups of the expanded rows
  @ViewChildren('viewMoreCollapseGroup')
  viewMoreCollapseGroups: QueryList<CollapseGroupComponent>;

  itemDetailDataArray = []; // itemDetailData object which is converted to array
  scoreDataArray; // scoreData object which is converted to array
  segmentScoreTypes; // scoreData object which is converted to array

  private collapseTableRowClass = 'table-row-collapse';
  private collapseViewMoreClass = 'view-more-collapse';

  private isInvalidUom: boolean = false; // mark uom as invalid if metric system is incorrect

  constructor(
    protected ref: ChangeDetectorRef,
    protected scorecardConnectorService: ScorecardConnectorService,
    protected brandService: BrandService,
    protected fb: UntypedFormBuilder,
    protected sanitizer: DomSanitizer,
  ) {
    super(ref, scorecardConnectorService, brandService, fb, sanitizer);
  }

  ngOnInit() {
    // create segmentDataForTypes for easy access to segment data
    _.forEach(packageTypeLookup, (type: any) => {
      this.segmentDataForTypes[type.option] = getPackagesSegmentDataForType(type.option, this.segmentData);
    });

    // update data for extendedInformation
    if (this.itemDetail) {
      _.defer(() => this.extInfo && this.extInfo.updateData(
        this.itemDetail,
        this.scoreData,
        this.scorecardData,
        this.priceSheets,
      ));
    }

    super.ngOnInit();
  }

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get itemDetailData(): Array<any> {
    return this.itemDetailDataArray;
  }

  get segmentScores(): any {
    return this.scoreDataArray;
  }

  get packagesScores(): any {
    return _.get(this.scoreData, this.pathToScores);
  }

  get isDirty(): boolean {
    return _.get(this.segmentForm, 'dirty', false) || this.extInfo && _.get(this.extInfo.segmentForm, 'dirty', false);
  }

  // redefine parent method
  get hiddenFields(): any[] {
    return _.filter(this.segmentData, (segmentItem: SegmentFieldItem) => {
      // @ts-ignore
        return !_.find(this.rowData, {fieldName: segmentItem.field_map}) &&
        !this.isFieldInEntireData(segmentItem.field_map, detailsData) &&
        !this.isFieldInEntireData(segmentItem.field_map, detailsShippingData) &&
        !this.isFieldInEntireData(segmentItem.field_map, hazardousData) &&
        !this.isFieldInEntireData(segmentItem.field_map, packingData);
    });
  }

  get selectedUnit(): any {
    // @ts-ignore
      return _.find(this.units, {option: this.metricSystem});
  }

  get isEmpty(): boolean {
    return !this.formArray?.value?.length;
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  public getSegmentFormData(): any {
    const packages = _.cloneDeep(this.formArray.value);
    _.forEach(packages, (pckg: any) => {
      // delete pckg.packageUomTitle;
    });
    return {
      packages,
      extendedInformation: this.extInfo && this.extInfo.getSegmentFormData(),
    };
  }

  // redefine parent method
  public getField(fieldMap: string, type?: string): any {
    if (!type || fieldMap === 'packageUom') { return super.getField(fieldMap); }
    return _.find(this.getSegmentDataForType(type), {field_map: fieldMap});
  }

  // redefine parent method
  public getFieldTitle(fieldMap: string, type?: string): string {
    if (!type || fieldMap === 'packageUom') { return super.getFieldTitle(fieldMap); }
    const field: any = this.getField(fieldMap, type);
    return field ? field.title : '';
  }

  // redefine parent method
  public getFieldLookup(fieldMap: string, type?: string): any[] {
    if (!type) { return super.getFieldLookup(fieldMap); }
    if (fieldMap === 'packageUom') { return this.getAvailableTypeLookup(); }
    if (fieldMap === 'packageUomTitle') { return this.getTitleTypeLookup(); }
    const field: any = this.getField(fieldMap, type);
    return field ? field.lookup : null;
  }

  public getAvailableTypeLookup() {
    // return available types
    const allLookups = super.getFieldLookup('packageUom');
    const usedLookups = _.map(this.formArray.value, (item: any) => item.packageUom);
    _.forEach(allLookups, (option: any) => {
      option.hidden = _.indexOf(usedLookups, option.option) >= 0;
    });
    return allLookups;
  }

  public getTitleTypeLookup() {
    // return available types
    const allLookups = super.getFieldLookup('packageUomTitle');
    return allLookups;
  }

  public updateData(
    itemDetail?: any,
    scoreData?: any,
    scorecardData?: any,
    priceSheets?: any,
  ) {
    super.updateData(
      itemDetail,
      scoreData,
      scorecardData,
      priceSheets,
    );

    if (this.extInfo instanceof ExtendedInformationComponent) {
      this.extInfo.updateData(
        itemDetail,
        scoreData,
        scorecardData,
        priceSheets,
      );
    }
  }

  public updateScoreData(scoreData: any, scorecardData?: any): void {
    super.updateScoreData(scoreData, scorecardData);

    this.scoreDataArray = convertPackagesScoreDataToArray(this.packagesScores);
    this.segmentScoreTypes = getAvailableSegmentScoreTypes(this.packagesScores);

    if (this.extInfo instanceof ExtendedInformationComponent) {
      this.extInfo.updateScoreData(scoreData, scorecardData);
    }

    this.ref.markForCheck();
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onItemControlStatusChange(status: any, fieldMap: string, index: number, type?: string) {
    const item = this.getField(fieldMap, type);
    this.scorecardConnectorService.controlStatusChanged.emit({
      status, index, item,
    });
  }

  onItemControlPopulatedChange(populated: any, itemName: string, index: number, type?: string) {
    const item = this.getField(itemName, type);
    this.scorecardConnectorService.controlPopulatedChanged.emit({
      populated, index, item,
    });
  }

  onUnitsChange(event) {
    this.metricSystem = event.option;
    this.updatePackageFormGroupUoms();
  }

  onItemControlValueChange(value: string, formGroup: UntypedFormGroup, fieldName: string) {
    // if it's dimensions field
    if (this.dimensionsFieldMaps.indexOf(fieldName) >= 0) {
      // if value is not empty
      if (!_.isNil(value) && value.length) {
        const dimensionsUom: string = this.getLengthUom(this.metricSystem);
        formGroup.patchValue({dimensionsUom});
      } else {
        // if value is empty and all dimensions are empty - clear dimensions uom
        if (!this.hasAtLeastOneDimension(formGroup)) {
          formGroup.patchValue({dimensionsUom: ''});
        }
      }
    } else if (this.weightsFieldMaps.indexOf(fieldName) >= 0) {
      // if it's weights field
      // if value is not empty
      if (!_.isNil(value) && value.length) {
        const weightUom: string = this.getWeightUom(this.metricSystem);
        formGroup.patchValue({weightUom});
      } else {
        // if value is empty and all dimensions are empty - clear dimensions uom
        if (!this.hasAtLeastOneWeight(formGroup)) {
          formGroup.patchValue({weightUom: ''});
        }
      }
    } else if (fieldName === 'packageUomTitle') {
      this.updatePackageUom(formGroup);
    }
    super.onItemControlValueChange(value, formGroup, fieldName);
  }

  onAddNewPackageType() {
    const fg = this.createItemFG({});
    this.formArray.push(fg);
    this.updatePackageUom(fg);
    this.formArray.markAsDirty();
    this.ref.markForCheck();
  }

  updatePackageUom(fg: UntypedFormGroup) {
    const packageUom = fg.value.packageUomTitle || 'EA';
    fg.patchValue({packageUom});
  }

  onDeletePackageClick(formGroup) {
    const index = this.formArray.controls.indexOf(formGroup);
    this.formArray.removeAt(index);
    this.formArray.markAsDirty();
    this.ref.markForCheck();
  }

  /*******************************************
  * COMPONENT PROTECTED METHODS
  *******************************************/

  // replace parent method:
  protected setData(itemDetail: any = {}): void {
    if (this.itemDetail) {
      this.itemDetailDataArray = _.flatten(this.convertItemDetailDataToArray(_.get(this.itemDetail, this.segmentName)));
      this.scoreDataArray = convertPackagesScoreDataToArray(this.packagesScores);
      this.segmentScoreTypes = getAvailableSegmentScoreTypes(this.packagesScores);
      // reset form array
      this.segmentForm.setControl(this.segmentName, this.fb.array([]));
      // add data item to formArray
      _.forEach(this.itemDetailData, (item: any, index: number) => {
        const fg = this.createItemFG(item);
        this.formArray.push(fg);

        // Emit event for conditional required rules change
        // because row content cells are hidden when it's collapsed
        _.forEach(_.keys(fg.controls), key => {

          fg.controls[key].statusChanges
            .pipe(takeWhile(() => this.isActive))
            .subscribe((status) => {
              const packageUom = fg.value.packageUom;
              const field = this.getFieldByMap(key, this.segmentDataForTypes[packageUom]);
              if (!field || !field.item_name) {
                return;
              }

              const conditionals = this.getRequiredConditionalsForField(field.item_name);
              if (conditionals) {
                this.scorecardConnectorService.conditionalRequiredDataChanged.emit({
                  [`${this.segmentName}.${field.item_name}`]: getFieldRequiredValue(conditionals, fg.controls),
                });
              }

              this.onItemControlStatusChange(status, field.field_map, index, packageUom);

              if (conditionals) {
                this.scorecardConnectorService.conditionalRequiredDataChanged.emit({
                  [`${this.segmentName}.${field.item_name}`]: getFieldRequiredValue(conditionals, fg.controls),
                });
              }

            });
        });

      });

      this.updateMetricSystem();
      this.showSegment();
    }
  }

  protected emitConditionalStatuses() {
    // go through all conditional fields and emit their statuses
    _.forEach(this.scorecardConditionals, conditional => {
      if (conditional.path.indexOf(`${this.segmentName}.`) !== 0) {
        return;
      }
      const fieldName = conditional.path.replace(`${this.segmentName}.`, '');
      const packageUom = fieldName.substr(0, fieldName.indexOf('.'));
      const index = _.findIndex(this.formArray.value, {packageUom});

      if (index < 0) {
        return;
      }

      const field = this.getFieldByName(fieldName, this.segmentData);
      if (!field) {
        return;
      }
      const formControl = _.get(this.formArray.at(index), `controls[${field.field_map}]`);

      if (!formControl) {
        return;
      }

      this.scorecardConnectorService.conditionalRequiredDataChanged.emit({
        [`${this.segmentName}.${field.item_name}`]: getFieldRequiredValue(conditional, _.get(this.formArray.at(index), 'controls')),
      });

      this.scorecardConnectorService.controlStatusChanged.emit({
        status: formControl.status,
        index,
        item: field,
      });

    });
  }

  // replace parent method:
  protected createItemModel(item: any): PackageModel {
    const model = new PackageModel();

    // Set artificial fields - packageUom and packageUomTitle
    // set packageUom control
    let typeField = this.getField('packageUom');
    let value = _.get(item, typeField.item_name, '');
    _.set(model, typeField.field_map, new UntypedFormControl(value, [])); // don't validate it

    // set packageUomTitle control
    typeField = this.getField('packageUomTitle');
    value = _.get(item, 'packageUom', 'EA');
    _.set(model, typeField.field_map, new UntypedFormControl(value, [])); // don't validate it

    // set other fields
    _.each(this.getSegmentDataForType(item.packageUom || 'EA'), (field: any) => {

      if (!field.field_map) { return; }
      const validators: any = this.getControlValidators(field);
      const val = _.get(item, field.item_name, '');
      _.set(model, field.field_map, new UntypedFormControl(val, validators));
    });

    return model;
  }

  /*
    * Define in the child
    * Used to update controls validation after scorecard changes
  */
  protected updateControlsValidation(): void {
    _.each(this.itemDetailData, (itemDetailItem: any, index: number) => {
      // get segment data for particular type
      _.each(this.getSegmentDataForType(itemDetailItem.packageUom), (item: SegmentFieldItem) => {
        const formControl: any = this.formArray.at(index).get(item.field_map);
        if (!formControl) { return; }
        const validators: any = this.getControlValidators(item);

        if (formControl.controls) {
          // FormArray
          _.each(formControl.controls, (control: AbstractControl) => {
            this.updateControlValidators(control, validators);
          });
        } else {
          // FormControl
          this.updateControlValidators(formControl, validators);
        }
      });
    });
    this.ref.markForCheck();
  }

  protected getCollapseGroupByName(name: string): CollapseGroupComponent|null {
    if (!this.collapseGroups) { return null; }
    return _.find(this.collapseGroups.toArray(), ['name', name]);
  }

  // replace parent method:
  protected collapseRow(index: number, collapse?: boolean, className?: string) {
    if (!className) {
      className = this.collapseTableRowClass + index;
    }
    const collapseGroup = this.getCollapseGroupByName(className);

    if (!collapseGroup) { return; }
    if (_.isNil(collapse)) { collapse = !collapseGroup.collapsed; }
    collapseGroup.setCollapsed(collapse);

    this.scorecardConnectorService.tableRowCollapsed.emit({
      index,
      collapsed: collapse,
    });

    this.ref.markForCheck();
  }

  /*
    * Expands nested score details according on selected item
    * Common handler for expanding nested scores
  */
  protected handleScoreDetailsExpand($event: any) {
    if (!_.has($event, 'index')) { return; }

    const fieldName = _.get($event, 'item.field_map');

    const isInViewMore = this.isFieldInEntireData(fieldName, hazardousData) ||
      this.isFieldInEntireData(fieldName, packingData);

    if (isInViewMore) {
      const viewMoreCollapse = _.find(this.viewMoreCollapseGroups.toArray(), ['name', `${this.collapseViewMoreClass}${$event.index}`]);
      if (viewMoreCollapse) {
        viewMoreCollapse.setCollapsed(!$event.expanded);
        this.ref.markForCheck();
      }
    }
    // wait until row expand and row controls rendering
    _.defer(() => super.handleScoreDetailsExpand($event));
  }

  protected getScoreItem(item: SegmentFieldItem, index: number|string): any {
    return _.get(this.segmentScores, getPackageScoreItemPath(index, item));
  }

  protected getScoreIndexItem(item: SegmentFieldItem, index: number|string, propertyIndex: number): any {
    return _.get(this.segmentScores, `${getPackageScoreItemPath(index, item)}[${propertyIndex}]`);
  }

  protected hasScoreForPackageType(type: string) {
    return _.indexOf(this.segmentScoreTypes, type) >= 0;
  }

  // redefine parent method
  protected getScorecardField(name: string): any {
    return _.find(this.scorecardFields, (scorecardField: any) => {
      return (scorecardField.path.indexOf(`${this.segmentName}.${name}`) === 0);
    });
  }

  // check whether the fiels is in score or not
  // replace this method - require with prefix 'packages'
  // field is from table-data
  protected hasItemInScores(field: any, type?: any): boolean {
    const segmentData = this.getSegmentDataForType(type);
    const item = this.getFieldByMap(field.fieldName, segmentData);
    if (!item) { return false; }
    return _.has(this.packagesScores, `packages.${item.item_name}`);
  }

  // get packageUom type from formGroup
  protected getFGType(formGroup: UntypedFormGroup) {
    return formGroup.get('packageUom').value;
  }

  // show field masked or not
  protected isMasked(field: any, formGroup: UntypedFormGroup) {
    if (!this.hasScores) { return false; }
    if (field.fieldName === 'packageUomTitle' || field.fieldName === 'packageUom') {
      return !this.hasScoreForPackageType(this.getFGType(formGroup));
    }
    return (!this.hasItemInScores(field, this.getFGType(formGroup)) || field.disabled);
  }

  protected getMaskClass(field: any, formGroup: UntypedFormGroup) {
    if (field.fieldName === 'packageUomTitle' || field.fieldName === 'packageUom') {
      if (!this.hasScoreForPackageType(this.getFGType(formGroup))) {
        return 'transparent';
      }
    }
    if (!this.hasItemInScores(field, this.getFGType(formGroup))) {
      return 'transparent';
    } else if (field.disabled) {
      return 'disabled';
    }
    return '';
  }

  protected hasValue(formGroup: UntypedFormGroup, fieldMap: string) {
    const fgValue = formGroup.value;
    return (!_.isNil(fgValue[fieldMap]) && fgValue[fieldMap].length > 0);
  }

  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  // returns only segments for certain type code from packageLookup: EA, PK, BX, CA, PL
  private getSegmentDataForType(type: string): SegmentFieldItem[] {
    return this.segmentDataForTypes[type];
  }

  private convertItemDetailDataToArray(itemDetailData: any): any[] {
    return convertPackagesDataToArray(itemDetailData);
  }

  private getSelectUom(fieldName: string, formGroup: UntypedFormGroup, packageType: string) {
    if (this.dimensionsFieldMaps.indexOf(fieldName) >= 0) {
      if (this.isInvalidUom) {
        const firstItem = _.get(this.itemDetailData, '[0]');
        if (!firstItem) {
          return null;
        }
        const itemData = _.get(firstItem, firstItem.packageUom);
        const firstItemDimensionsUOM = _.get(itemData, 'dimensions.uom');
        return firstItemDimensionsUOM;
      }
      return this.getOptionTitle('dimensionsUom', formGroup, packageType);
    } else if (this.weightsFieldMaps.indexOf(fieldName) >= 0) {
      if (this.getIsInvalidUom(fieldName)) {
        const firstItem = _.get(this.itemDetailData, '[0]');
        if (!firstItem) {
          return null;
        }
        const itemData = _.get(firstItem, firstItem.packageUom);
        const firstItemWeightUOM = _.get(itemData, 'weights.uom');
        return firstItemWeightUOM;
      }
      return this.getOptionTitle('weightUom', formGroup, packageType);
    }
    return null;
  }

  private updatePackageFormGroupUoms() {
    const dimensionsUom: string = this.getLengthUom(this.metricSystem);
    const weightUom: string = this.getWeightUom(this.metricSystem);
    _.forEach(this.formArray.controls, (formGroup: UntypedFormGroup) => {
      if (this.hasAtLeastOneDimension(formGroup)) {
        formGroup.patchValue({dimensionsUom});
      } else {
        formGroup.patchValue({dimensionsUom: ''});
      }

      if (this.hasAtLeastOneWeight(formGroup)) {
        formGroup.patchValue({weightUom});
      } else {
        formGroup.patchValue({weightUom: ''});
      }
    });
    this.formArray.markAsDirty();
    this.ref.markForCheck();
  }

  // check if formGroup has non-empty
  private hasAtLeastOneDimension(formGroup: UntypedFormGroup) {
    return _.some(this.dimensionsFieldMaps, (fieldMap: string) => {
      return this.hasValue(formGroup, fieldMap);
    });
  }

  // check if formGroup has non-empty 'dimensionalWeight' or 'weight'
  private hasAtLeastOneWeight(formGroup: UntypedFormGroup) {
    return _.some(this.weightsFieldMaps, (fieldMap: string) => {
      return this.hasValue(formGroup, fieldMap);
    });
  }

  private getLengthUom(metricSystem: string): string {
    return metricSystem === metrics.STANDART ? 'IN' : 'CM';
  }

  private getWeightUom(metricSystem: string): string {
    return metricSystem === metrics.STANDART ? 'PG' : 'GT';
  }

  private getOptionTitle(fieldName: string, formGroup: UntypedFormGroup, packageType: string): string {
    const option = _.get(formGroup.get(fieldName), 'value');
    if (!option) { return null; }
    const fieldLookup = this.getFieldLookup(fieldName, packageType);
    if (!fieldLookup) { return null; }
    return _.get(_.find(fieldLookup, {option}), 'title');
  }

  // for hazardousData, packingData, detailsData
  private isFieldInEntireData(fieldName: string, data: any[]) {
    return _.some(_.map(data, (item: any) => {
      return !!_.find(item.cols, {fieldName}) || _.some(item.cols, (colData: any) => {
        return _.indexOf(colData.combined, fieldName) >= 0;
      });
    }));
  }

  // update value of selected metric system on segment load
  private updateMetricSystem() {
    // try to get metric data from first data item
    const firstItem = _.get(this.itemDetailData, '[0]');
    if (!firstItem) {
      this.metricSystem = metrics.STANDART;
    } else {
      const itemData = _.get(firstItem, firstItem.packageUom);
      // check dimensions uom
      const firstItemDimensionsUOM = _.get(itemData, 'dimensions.uom');
      if (!firstItemDimensionsUOM) {
        // if no dimensions uom - check weight uom
        const firstItemWeightUOM = _.get(itemData, 'weights.uom');
        if (!firstItemWeightUOM) {
          // if no weight uom - set default
          this.metricSystem = metrics.STANDART;
        } else {
          // set certain metric system
          this.metricSystem = firstItemWeightUOM === 'PG' ? metrics.STANDART : metrics.METRIC;
        }
      } else {
        // set certain metric system
        if (firstItemDimensionsUOM === 'IN') {
          this.metricSystem = metrics.STANDART;
        } else if (firstItemDimensionsUOM === 'CM') {
          this.metricSystem = metrics.METRIC;
        } else {
          this.metricSystem = metrics.STANDART;
          this.isInvalidUom = true;
        }
      }
    }
    this.updatePackageFormGroupUoms();
  }

  private getIsInvalidUom(fieldName: string): boolean {
    if (this.weightsFieldMaps.indexOf(fieldName) >= 0) {
      const firstItem = _.get(this.itemDetailData, '[0]');
      if (!firstItem) {
        return this.isInvalidUom;
      }
      const itemData = _.get(firstItem, firstItem.packageUom);
      const firstItemWeightUOM = _.get(itemData, 'weights.uom');
      return !(firstItemWeightUOM === 'PG' || firstItemWeightUOM === 'GT');
    }
    return this.isInvalidUom;
  }
}
